import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  authLogin,
  authGoogleSession,
  authLogout,
  authMe,
  getAuthToken,
  setAuthToken,
} from "@/lib/api";

const AuthContext = createContext(null);

/**
 * States:
 *   user === undefined  → still checking (initial mount)
 *   user === null       → not authenticated
 *   user === object     → authenticated
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(null);
      return;
    }
    try {
      const me = await authMe();
      setUser(me);
    } catch {
      setAuthToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Google Auth callback lands with `#session_id=XXX`. Exchange it here
    // BEFORE calling /auth/me so we don't flash the login page.
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      (async () => {
        try {
          const data = await authGoogleSession(decodeURIComponent(m[1]));
          setAuthToken(data.token);
          setUser(data.user);
        } catch (ex) {
          const status = ex?.response?.status;
          const detail = ex?.response?.data?.detail;
          let msg = "Google Sign-In failed. Please try again.";
          if (status === 403) {
            msg = typeof detail === "string"
              ? detail
              : "This Google account is not authorised to access this CRM.";
          } else if (status === 401) {
            msg = "Google session expired. Please try again.";
          }
          sessionStorage.setItem("crm_login_error", msg);
          setAuthToken(null);
          setUser(null);
        } finally {
          // Clear the hash so a page refresh doesn't retry with a burnt session_id
          window.history.replaceState(null, "", window.location.pathname);
        }
      })();
      return;
    }
    refresh();
  }, [refresh]);

  // When any API call sees a 401, the axios interceptor fires this event.
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener("crm-auth-expired", handler);
    return () => window.removeEventListener("crm-auth-expired", handler);
  }, []);

  const login = async (password) => {
    const data = await authLogin(password);
    setAuthToken(data.token);
    setUser(data.user);
    return data;
  };

  const loginWithGoogleSessionId = async (session_id) => {
    const data = await authGoogleSession(session_id);
    setAuthToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch {
      /* stateless — best-effort */
    }
    setAuthToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogleSessionId, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
